import { MessageSquareDashed, SearchX } from 'lucide-react';
import ConversationItem from './ConversationItem';

function SkeletonRows({ count = 6 }) {
  return (
    <ul aria-hidden="true" className="space-y-1 px-2 pt-1.5">
      {Array.from({ length: count }, (_, index) => (
        <li key={index} className="flex items-start gap-3 rounded-xl px-3 py-2.5">
          <span className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-surface-3" />
          <span className="min-w-0 flex-1 space-y-2 pt-0.5">
            <span className="block h-3 w-1/2 animate-pulse rounded bg-surface-3" />
            <span className="block h-2.5 w-4/5 animate-pulse rounded bg-surface-3" />
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Independently scrollable conversation list.
 *
 * `flex-1 min-h-0` is what lets the list scroll inside the flex column without
 * pushing the sidebar header off screen.
 */
export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  isFiltered,
  onResetFilters,
  loading = false,
}) {
  if (loading && conversations.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <span className="sr-only" role="status">
          Loading conversations…
        </span>
        <SkeletonRows />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-surface-3 text-ink-3">
          {isFiltered ? <SearchX size={20} /> : <MessageSquareDashed size={20} />}
        </span>
        <div>
          <p className="text-[13px] font-medium text-ink">
            {isFiltered ? 'No conversations match' : 'No conversations yet'}
          </p>
          <p className="mt-0.5 text-[12px] text-ink-3">
            {isFiltered
              ? 'Try a different search or tab.'
              : 'Chats you start from a listing or order appear here.'}
          </p>
        </div>
        {isFiltered && (
          <button
            type="button"
            onClick={onResetFilters}
            className="rounded-lg border border-line px-3 py-1.5 text-[12px] font-medium text-ink-2 transition hover:border-brand-300 hover:bg-brand-soft hover:text-accent"
          >
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <ul
      role="list"
      aria-label="Conversations"
      className="z2-scroll min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pt-1.5 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    >
      {conversations.map((conversation) => (
        <ConversationItem
          key={conversation.id}
          conversation={conversation}
          isActive={conversation.id === selectedId}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}
